import { Table, Box, SpaceBetween, Link, PropertyFilter, Pagination } from '@amzn/awsui-components-react/polaris';
import React, { useEffect, useMemo, useState } from 'react';
import TaskStatusIndicator from '../shared/TaskStatusIndicator';
import TPENavigator from '../TPENavigator';
import DASHBOARD_CONSTANTS from './dashboardConstants';
import { Task, TasksSearchResult } from 'src/models/dashboard/Task';
import { PropertyFilterQuery } from '@amzn/awsui-collection-hooks';
import { convertObjectsToFilterOptions, getDefaultDateFilterOperators, OPERATORS_EQUALS_ONLY, replaceFilterAccessorFieldWithBDField, getPropertyValueLabel } from './helpers/PropertyFilterHelper';
import CONSTANTS from 'src/utils/constants';
import CopyToClipboard from '../CopyToClipboard';

export default function OpenTasksGrid(props: { 
    openTasksResult: TasksSearchResult, 
    isLoading: boolean, 
    dashboardView?: boolean,
    gridHeader?: any,
    showFilter?: boolean,
    selectedNotifications: any[],
    setSelectedNotifications: (selectedItems: any[]) => any,
    onFilterQueryChanged?: (query: PropertyFilterQuery) => void,
    enableClientSidePagination?: boolean,
    isAllTasksView?: boolean,
    onFilteredCountChange?: (count: number) => void
 }) {
  const { openTasksResult, isLoading, dashboardView = false, gridHeader, showFilter = false, selectedNotifications, setSelectedNotifications, onFilterQueryChanged, enableClientSidePagination = false, isAllTasksView = false, onFilteredCountChange } = props;

  const [navigateURL, setNavigateURL] = useState('');

  const navigateToEntity = (entityType: string, entityId: string) => {
    if (entityType == DASHBOARD_CONSTANTS.ENTITY_TYPES.CDT) {
      setNavigateURL(`${CONSTANTS.PAGE_NAV.CUSTOM_DATA_TABLE_DETAILS.URL}/${btoa(entityId)}`);
    } else if (entityType == DASHBOARD_CONSTANTS.ENTITY_TYPES.CALCULATION) {
      setNavigateURL(`${CONSTANTS.PAGE_NAV.PERIOD_CHARGES.URL}`);
    }
  }

  const colDefs: any[] = useMemo(() => [
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_ITEM.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_ITEM.HEADER,
          cell: (item: Task) => (
            <SpaceBetween direction="horizontal" size="xs">
              <Link variant="primary" href="javascript:void(0)" onFollow={() => navigateToEntity(item.entityType, item.entityId)}>{item.entityName}</Link>
              {item.notificationType === 'BEGINNING_BALANCE' && (
                <CopyToClipboard textToBeCopied={item.entityName} popoverMessage="Action item copied" />
              )}
            </SpaceBetween>
          ),
          width: '20%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.HEADER,
          cell: (item: Task) => (DASHBOARD_CONSTANTS.ACTION_TYPE_TO_DISPLAY_MAP as any)[item.notificationType],
          width: '15%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.TASK_STATUS.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.TASK_STATUS.HEADER,
          cell: (item: Task) => <TaskStatusIndicator status={item.taskStatus} />,
          width: '10%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.DUE_DATE.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.DUE_DATE.HEADER,
          cell: (item: Task) => item.dueDateDisplay,
          width: '10%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_NAME.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_NAME.HEADER,
          cell: (item: Task) => item.workbookName || '',
          width: '15%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_OWNER.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_OWNER.HEADER,
          cell: (item: Task) => item.workbookOwner || '',
          width: '15%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.CLI_ASSIGNEE.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.CLI_ASSIGNEE.HEADER,
          cell: (item: Task) => item.cliAssignee || '',
          width: '15%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.RELATED_CLI.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.RELATED_CLI.HEADER,
          cell: (item: Task) => item.relatedCalculationNumber || '',
          width: '12%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.VALUE.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.VALUE.HEADER,
          cell: (item: Task) => item.cdtValueName || '',
          width: '12%'
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.PERIOD.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.PERIOD.HEADER,
          cell: (item: Task) => item.periodId || '',
          width: '10%'
      }
  ], [openTasksResult]);

  const filteringProperties = useMemo(() => [
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_ITEM.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_ITEM.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_ITEM.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.TASK_STATUS.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.TASK_STATUS.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.TASK_STATUS.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.DUE_DATE.ACCESSOR,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.DUE_DATE.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.DUE_DATE.HEADER} values`,
        operators: getDefaultDateFilterOperators()
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_NAME.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_NAME.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_NAME.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_OWNER.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_OWNER.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.WORKBOOK_OWNER.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.CLI_ASSIGNEE.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.CLI_ASSIGNEE.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.CLI_ASSIGNEE.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.RELATED_CLI.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.RELATED_CLI.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.RELATED_CLI.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.VALUE.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.VALUE.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.VALUE.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.PERIOD.ACCESSOR,
        operators: OPERATORS_EQUALS_ONLY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.PERIOD.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.PERIOD.HEADER} values`
    },
  ], []);

  const filteringOptions = useMemo(
    () => convertObjectsToFilterOptions(openTasksResult?.items || [])
    ,[openTasksResult])

  const [filterQuery, setFilteringQuery] = useState({
    tokens: [],
    operation: "and"
  } as PropertyFilterQuery);
  
  // Client-side pagination state
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const allItems = openTasksResult?.items || [];
  
  // Client-side filtering
  const filteredItems = useMemo(() => {
    if (!enableClientSidePagination || filterQuery.tokens.length === 0) {
      return allItems;
    }
    
    return allItems.filter((item: Task) => {
      const matchFunction = (token: any) => {
        const itemValue = (item as any)[token.propertyKey];
        let filterValue = token.value;
        
        // Handle display value mapping for action type
        if (token.propertyKey === DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.ACTION_TYPE.ACCESSOR) {
          const displayValue = getPropertyValueLabel(token.propertyKey, itemValue);
          return displayValue === filterValue;
        }
        
        // Handle date comparison
        if (token.propertyKey === DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS.DUE_DATE.ACCESSOR) {
          const itemDate = (item as any).dueDateDisplay;
          return itemDate === filterValue;
        }
        
        // Default string comparison for new fields and existing ones
        return itemValue === filterValue;
      };

      // Apply AND or OR logic based on the operation
      return filterQuery.operation === "or"
        ? filterQuery.tokens.some(matchFunction)
        : filterQuery.tokens.every(matchFunction);
    });
  }, [allItems, filterQuery, enableClientSidePagination]);
  
  // Calculate paginated items
  const items = useMemo(() => {
    if (!enableClientSidePagination) {
      return allItems;
    }
    const itemsToPage = filteredItems;
    const startIndex = (currentPageIndex - 1) * DASHBOARD_CONSTANTS.ALL_TASKS_CLIENT_SIDE_PAGE_SIZE;
    const endIndex = startIndex + DASHBOARD_CONSTANTS.ALL_TASKS_CLIENT_SIDE_PAGE_SIZE;
    return itemsToPage.slice(startIndex, endIndex);
  }, [filteredItems, currentPageIndex, enableClientSidePagination, allItems]);


  useEffect(() => {
        // Only send filter to backend if not doing client-side pagination
        if (onFilterQueryChanged && !enableClientSidePagination){
            onFilterQueryChanged(replaceFilterAccessorFieldWithBDField(filterQuery, DASHBOARD_CONSTANTS.OPEN_TASK_FIELDS));
        }
        // Reset to first page when filter changes
        setCurrentPageIndex(1);
    }, [filterQuery])

  // Reset to first page when the data source changes (e.g., toggling all users view)
  useEffect(() => {
    if (enableClientSidePagination) {
      setCurrentPageIndex(1);
    }
  }, [openTasksResult, enableClientSidePagination])

  // Notify parent of filtered count changes
  useEffect(() => {
    if (onFilteredCountChange && enableClientSidePagination) {
      onFilteredCountChange(filteredItems.length);
    }
  }, [filteredItems.length, onFilteredCountChange, enableClientSidePagination])


  return (
    <>
    <TPENavigator path={navigateURL} />
    <Table
        columnDefinitions={colDefs}
        enableKeyboardNavigation
        wrapLines={true}
        items={items}
        loadingText="Loading"
        loading={isLoading}
        header={gridHeader}
        selectionType='multi'
        isItemDisabled={(item: Task) => !item.allowStatusUpdate}
        onSelectionChange={({ detail }) => setSelectedNotifications(detail.selectedItems)}
        selectedItems={selectedNotifications}
        empty={
            <Box
            margin={{ vertical: "xs" }}
            textAlign="center"
            color="inherit"
            >
            <SpaceBetween size="m">
                <b>No Open Tasks</b>
                <span>All tasks have been completed</span>
            </SpaceBetween>
            </Box>
        }
        variant={dashboardView ? 'embedded' : 'container'}
        filter={ showFilter &&
            <PropertyFilter
              filteringPlaceholder="Find Tasks"
              expandToViewport
              query={filterQuery}
              onChange={({detail}) => setFilteringQuery(detail)}
              countText={isLoading? '' : enableClientSidePagination ? `${filteredItems.length} match${filteredItems.length !== 1 ? 'es' : ''}` : `${allItems.length} match${allItems.length !== 1 ? 'es' : ''}`}
              filteringProperties={filteringProperties as any[]}
              filteringOptions={filteringOptions}
            />
          }
        pagination={enableClientSidePagination && filteredItems.length > 0 && 
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            pagesCount={Math.ceil(filteredItems.length / DASHBOARD_CONSTANTS.ALL_TASKS_CLIENT_SIDE_PAGE_SIZE)}
          />
        }
      />
      </>
    )
}



